import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRevalidator } from 'react-router';

import { AccountProfileElement, LoggedInTeaserElement } from '@shared/dream-components/src';

import StaticPage from '~/components/StaticPage';
import type { Site } from '~/interfaces/site';

interface Props {
  site: Site;
  loggedInSubscriberId: string;
}

const ReadOnlyPage = ({ site, loggedInSubscriberId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state: revalidateState } = useRevalidator();
  const isRevalidating = revalidateState === 'loading';

  const primaryThemeBackgroundColor = site.primary_theme.background_color;
  const primaryThemeTextOnBackgroundColor = site.primary_theme.text_on_background_color;

  return (
    <div
      className="bg-gray-100"
      style={
        {
          '--pt-background-color': primaryThemeBackgroundColor,
          '--pt-text-on-background-color': primaryThemeTextOnBackgroundColor,
        } as CSSProperties
      }
    >
      <StaticPage site={site} forceShowNavbar forceShowFooter>
        <div className="flex flex-col gap-6 w-full px-6 py-4 mx-auto min-h-dvh h-auto sm:py-8 sm:px-0 sm:w-2/3">
          <AccountProfileElement
            element={{
              type: 'accountProfile',
              attrs: {
                data: {
                  email: '-',
                  firstName: undefined,
                  lastName: undefined,
                  avatarUrl: undefined,
                },
              },
            }}
            isLoading={isRevalidating}
            showActions={false}
            translations={{
              'manage.account_profile.title': t('manage.account_profile.title'),
              'manage.account_profile.description': t('manage.account_profile.description'),
              'manage.account_profile.form.email_label': t('manage.account_profile.form.email_label'),
              'manage.account_profile.form.name_label': t('manage.account_profile.form.name_label'),
              'manage.account_profile.dropdown_options.edit_profile_label': t(
                'manage.account_profile.dropdown_options.edit_profile_label',
              ),
              'manage.account_profile.dropdown_options.reset_password_label': t(
                'manage.account_profile.dropdown_options.reset_password_label',
              ),
              'manage.account_profile.dropdown_options.logout': t('manage.account_profile.dropdown_options.logout'),
            }}
            isLive
            canResetPassword={false}
          >
            {null}
          </AccountProfileElement>

          <LoggedInTeaserElement
            element={{
              type: 'loggedInTeaser',
            }}
            title={t('manage.read_only.title')}
            buttonLabel={t('manage.read_only.button_label')}
            onClickButton={() => navigate(`/subscribe/${loggedInSubscriberId}/manage`)}
            translations={{
              'billing.current_plan.title': t('billing.current_plan.title'),
              'billing.summary.empty_state': t('billing.summary.empty_state'),
              'billing.summary.title': t('billing.summary.title'),
              'billing.summary.last_payment_heading': t('billing.summary.last_payment_heading'),
              'manage.preferences.title': t('manage.preferences.title'),
              'manage.preferences.description': t('manage.preferences.description'),
              'referrals.summary.title': t('referrals.summary.title'),
              'referrals.summary.description': t('referrals.summary.description'),
            }}
          >
            {null}
          </LoggedInTeaserElement>
        </div>
      </StaticPage>
    </div>
  );
};

export default ReadOnlyPage;
